import { formatDistanceToNow } from "date-fns";
import { AtSign, CircleDot, Clock, StickyNote, Truck, TriangleAlert, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NotificationRow } from "@/hooks/useNotifications";

interface NotificationItemProps {
  notification: NotificationRow;
  onClick?: () => void;
}

function iconForType(type: string) {
  switch (type) {
    case "order_note_mention":
      return AtSign;
    case "trailer_migration":
      return Truck;
    case "tractor_oos_unassigned":
      return TriangleAlert;
    case "order_status_changed":
      return CircleDot;
    case "order_assignment_changed":
      return UserCheck;
    case "order_schedule_changed":
      return Clock;
    case "order_note_added":
    default:
      return StickyNote;
  }
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const Icon = iconForType(notification.type);
  const isUnread = !notification.read_at;
  const timeLabel = formatDistanceToNow(new Date(notification.created_at), { addSuffix: false });

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors",
        isUnread ? "bg-accent/20" : "bg-background"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-muted p-2 shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold leading-snug">{notification.title}</p>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-xs text-muted-foreground">{timeLabel}</span>
              {isUnread && (
                <span className="h-2 w-2 rounded-full bg-primary inline-block" aria-label="Unread" />
              )}
            </div>
          </div>
          {(notification.actor_email || notification.body) && (
            <div className="mt-2 rounded-md bg-muted/40 px-2.5 py-2 text-xs space-y-1">
              {notification.actor_email && (
                <p className="text-muted-foreground">{notification.actor_email}</p>
              )}
              {notification.body && (
                <p
                  className={
                    'text-foreground whitespace-pre-wrap break-words ' +
                    // External-update notifications carry a bulleted change list;
                    // give them room instead of clamping to 3 lines.
                    (notification.type === 'taas_order_updated' ? 'line-clamp-none' : 'line-clamp-3')
                  }
                >
                  {notification.body}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}